import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Images, MapPin, Tag } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import { cn } from '../../lib/utils';
import brandConfig from '../../config/brandConfig';

const ProjectHeader = ({ activeTab, onTabChange, onOpenPhotos, onOpenAbout, onOpenOffers, onClosePhotos, onCloseAbout, onCloseOffers, onResetView }) => {
  const { project, loading } = useProject();

  if (loading || !project) {
    return (
      <div className="bg-white shadow-sm border-b border-gray-200 py-2 px-3 sm:px-4">
        <div className="animate-pulse flex items-center space-x-2">
         
            <img
              src="/images/android-chrome-192x192.png"
              alt="lotespro.com Logo"
              className="w-7 h-7 rounded-lg object-cover shadow-sm"
            />
       
          <div className="flex-1">
            <div className="h-3 bg-gray-200 rounded w-1/3 mb-1"></div>
            <div className="h-2 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  const navItems = [
    {
      id: 'proyecto',
      label: 'Acerca del proyecto',
      icon: Building2,
      action: onOpenAbout,
      closeAction: onClosePhotos
    },
    {
      id: 'galeria',
      label: 'Galería',
      icon: Images,
      action: onOpenPhotos,
      closeAction: onCloseAbout
    },
    {
      id: 'ofertas',
      label: 'Ofertas',
      icon: Tag,
      action: onOpenOffers,
      closeAction: onCloseOffers
    }
  ];

  const handleNavClick = (item) => {
    if (onClosePhotos) onClosePhotos();
    if (onCloseAbout) onCloseAbout();
    if (onCloseOffers) onCloseOffers();

    setTimeout(() => {
      if (item.action) {
        item.action();
      } else {
        onTabChange(item.id);
      }
    }, 100);
  };

  return (
    <div className="bg-white shadow-sm border-b border-gray-200 relative z-50">
      <div className="px-3 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between py-2 min-h-[2.5rem]">
          {/* Logo y nombre del proyecto */}
          <div className="flex items-center space-x-2.5 min-w-0 flex-1">
            {/* Logo de la organización o logo por defecto */}
            {/* <motion.a
              href="/"
              className="flex-shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={project.organization_name || brandConfig.name}
            > */}
              <img
                src={project.organization_logo || '/images/android-chrome-192x192.png'}
                alt={project.organization_name || brandConfig.name}
                className="w-10 h-10 rounded-full object-cover shadow-sm hover:shadow-md transition-shadow"
                onError={(e) => {
                  // Fallback to default logo if organization logo fails to load
                  e.target.src = '/images/android-chrome-192x192.png';
                }}
              />
            {/* </motion.a> */}

            {/* Separador */}
            {/* <div className="hidden sm:block w-px h-7 bg-gray-200"></div> */}

            {/* Información del proyecto */}
            <div className="flex-1 min-w-0">
              <button
                onClick={onResetView}
                className="w-full text-left group"
                title="Volver a la vista inicial del proyecto"
              >
                <h1 className="text-sm sm:text-base font-bold text-gray-900 truncate leading-tight flex items-center gap-1.5 group-hover:text-orange-600 transition-colors">
                  <Building2 className="w-4 h-4 text-orange-600 flex-shrink-0" />
                  <span className="truncate">{project.name}</span>
                </h1>
              </button>
              {project.location && (
                <p className="text-xs text-gray-600 truncate leading-tight mt-1 flex items-center gap-1.5">
                  <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  <span className="truncate">{project.location}</span>
                </p>
              )}
            </div>
          </div>

          {/* Navigation menu - Desktop */}
          <nav className="hidden md:flex items-center ml-4">
            <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;

                return (
                  <motion.button
                    key={item.id}
                    onClick={() => handleNavClick(item)}
                    className={cn(
                      'relative px-3 py-1.5 text-xs font-semibold transition-all duration-200 flex items-center gap-1.5 whitespace-nowrap rounded-md',
                      isActive
                        ? 'bg-white text-orange-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                    )}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Icon className={cn(
                      'w-3.5 h-3.5',
                      isActive ? 'text-orange-600' : 'text-gray-500'
                    )} />
                    <span>{item.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </nav>

          {/* Mobile navigation - dropdown menu */}
          <div className="md:hidden ml-2">
            <select
              value={activeTab || ''}
              onChange={(e) => {
                const selectedItem = navItems.find(item => item.id === e.target.value);
                if (selectedItem) {
                  handleNavClick(selectedItem);
                }
              }}
              className="text-xs font-semibold px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 shadow-sm"
            >
              <option value="" disabled>Menú</option>
              {navItems.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectHeader;
